import type { MatchConfig } from '../data/matchConfig.js';
import { MATCH_CONFIG_V0_3 } from '../data/matchConfig.js';
import { isSignalSurgeActive } from '../data/signalSurge.js';
import { Simulation } from '../simulation/Simulation.js';
import type { MatchState } from '../simulation/State.js';
import { TICK_MS, snapshot } from '../simulation/State.js';
import type { Command } from '../simulation/commands/Command.js';
import type { PlayerId } from '../simulation/ids.js';
import { tickOf } from '../simulation/ids.js';

import { diffCriticalFlips } from './CriticalFlipEmitter.js';
import type { MatchHost, MatchLifecycleEvent, PlayerView, Unsubscribe } from './MatchHost.js';

// V1.5+ Slice 3 — the emitter needs `nowMs` to derive the wall-clock
// countdown for `link_cut` banners. `Date.now()` is a stable cross-platform
// choice — the host runs in Node (server) AND the browser (local mode), and
// `performance.now()` epochs differ between the two. The banner only needs
// monotonic-ish ms relative to the rest of the V2 lifecycle clocks
// (`opponent_disconnected.closesAtMs` already uses `Date.now()`).
function nowMsForEmitter(): number {
  return Date.now();
}

// In-process host. Owns the Simulation. The accumulator is fed by the client's rAF
// loop via update(nowMs); no setInterval. V0.2 has two observers (one per player) and
// no fog, so each observer receives the same deep-cloned snapshot — invariant: the
// snapshot emitted to subscribers is never the live mutable state.
//
// V0.3 adds match_ended emission: the first tick the simulation's outcome flips from
// null, the host emits the lifecycle event. Subsequent ticks are no-ops in the
// simulation (Simulation.tick early-exits on outcome != null), so subscribers continue
// to observe the terminal snapshot but no further state changes occur.
export class LocalMatchHost implements MatchHost {
  private sim: Simulation | null = null;
  private accumulatorMs = 0;
  private lastUpdateMs: number | null = null;
  private running = false;
  private pending: Command[] = [];
  private endedEmitted = false;
  private overtimeStartedEmitted = false;
  // V1.5+ Slice 8 — Signal Storm Endgame. Same once-per-host-run flag shape
  // as overtimeStartedEmitted. Fires on the rising edge phase ===
  // 'signal_storm'.
  private signalStormStartedEmitted = false;
  // V1.5+ Signal Surge — short 5:30 capture-speed window. Same once-per-
  // host-run flag shape; fires on the rising edge matchSeconds crossing
  // SIGNAL_SURGE_START_SEC.
  private signalSurgeStartedEmitted = false;
  // V1.5 doctrine pick window. Each transition fires at most once per host-run.
  private doctrineOpenEmitted = false;
  private doctrineClosedEmitted = false;
  private snapshotListeners = new Set<(s: PlayerView, p: PlayerId) => void>();
  private lifecycleListeners = new Set<(e: MatchLifecycleEvent) => void>();
  private observers: PlayerId[] = [];
  // V1.5+ Slice 3 — diff source for CriticalFlipEmitter. Cached cross-tick
  // so the rising-edge guards have a consistent prior-state reference.
  // Cleared on start() so a re-run doesn't carry stale state from a prior
  // host-run.
  private prevCriticalFlipSnap: MatchState | null = null;

  start(seed: number, config: MatchConfig = MATCH_CONFIG_V0_3): void {
    this.sim = new Simulation(seed, config);
    this.accumulatorMs = 0;
    this.lastUpdateMs = null;
    this.pending = [];
    this.endedEmitted = false;
    this.overtimeStartedEmitted = false;
    this.signalStormStartedEmitted = false;
    this.signalSurgeStartedEmitted = false;
    this.doctrineOpenEmitted = false;
    this.doctrineClosedEmitted = false;
    this.prevCriticalFlipSnap = null;
    this.running = true;
    this.emitLifecycle({ kind: 'started', tick: this.sim.state.tick });
    this.broadcastSnapshot();
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    const tick = this.sim !== null ? this.sim.state.tick : tickOf(0);
    this.emitLifecycle({ kind: 'stopped', tick });
  }

  submit(playerId: PlayerId, command: Command): void {
    if (!this.running) return;
    if (command.issuedBy !== playerId) return;
    this.pending.push(command);
  }

  subscribe(listener: (snapshot: PlayerView, playerId: PlayerId) => void): Unsubscribe {
    this.snapshotListeners.add(listener);
    return () => {
      this.snapshotListeners.delete(listener);
    };
  }

  onLifecycle(handler: (event: MatchLifecycleEvent) => void): Unsubscribe {
    this.lifecycleListeners.add(handler);
    return () => {
      this.lifecycleListeners.delete(handler);
    };
  }

  registerObserver(playerId: PlayerId): void {
    if (!this.observers.includes(playerId)) this.observers.push(playerId);
  }

  update(nowMs: number): void {
    if (!this.running || this.sim === null) return;
    if (this.lastUpdateMs === null) {
      this.lastUpdateMs = nowMs;
      return;
    }
    // After the match ends, freeze the accumulator. Subscribers already have the
    // terminal snapshot and 'match_ended' has been emitted; further ticks are no-ops.
    if (this.sim.state.outcome !== null) {
      this.lastUpdateMs = nowMs;
      this.accumulatorMs = 0;
      return;
    }
    const elapsed = Math.max(0, nowMs - this.lastUpdateMs);
    this.lastUpdateMs = nowMs;
    // Cap accumulator so we don't spiral after a tab unfreeze.
    this.accumulatorMs = Math.min(this.accumulatorMs + elapsed, TICK_MS * 5);
    while (this.accumulatorMs >= TICK_MS) {
      this.accumulatorMs -= TICK_MS;
      const batch = this.pending;
      this.pending = [];
      this.sim.tick(batch);
      this.broadcastSnapshot();
      this.maybeEmitCriticalFlips();
      this.maybeEmitDoctrineWindow();
      this.maybeEmitSignalStormStarted();
      this.maybeEmitSignalSurgeStarted();
      this.maybeEmitOvertimeStarted();
      this.maybeEmitMatchEnded();
      if (this.sim.state.outcome !== null) {
        this.accumulatorMs = 0;
        break;
      }
    }
  }

  // V1.5+ Slice 3 — defender-side critical-flip alerts. Diffs the
  // post-tick snapshot against the prior tick's snapshot and broadcasts
  // one lifecycle event per rising-edge transition. Snapshot caching
  // mirrors how `broadcastSnapshot` already deep-clones via `snapshot()`,
  // so the diff source is stable across the per-recipient projection
  // boundary `ColyseusMatchHost` adds on top. The cache is cleared on
  // every `start()` so a re-run doesn't carry events from a prior match.
  private maybeEmitCriticalFlips(): void {
    if (this.sim === null) return;
    const next = snapshot(this.sim.state);
    const events = diffCriticalFlips(this.prevCriticalFlipSnap, next, nowMsForEmitter());
    this.prevCriticalFlipSnap = next;
    for (const e of events) this.emitLifecycle(e);
  }

  private maybeEmitOvertimeStarted(): void {
    if (this.sim === null) return;
    if (this.overtimeStartedEmitted) return;
    if (this.sim.state.phase !== 'overtime') return;
    this.overtimeStartedEmitted = true;
    this.emitLifecycle({ kind: 'overtime_started', tick: this.sim.state.tick });
  }

  // V1.5+ Slice 8 — Signal Storm Endgame. Mirrors maybeEmitOvertimeStarted
  // exactly: once-per-host-run rising edge on the storm phase. The HUD
  // reacts by mounting a 60-second purple banner; Sfx plays the
  // signal_storm_started cue for every viewer.
  private maybeEmitSignalStormStarted(): void {
    if (this.sim === null) return;
    if (this.signalStormStartedEmitted) return;
    if (this.sim.state.phase !== 'signal_storm') return;
    this.signalStormStartedEmitted = true;
    this.emitLifecycle({ kind: 'signal_storm_started', tick: this.sim.state.tick });
  }

  // V1.5+ Signal Surge — fires once at SIGNAL_SURGE_START_SEC (5:30) on the
  // rising matchSeconds edge. Capture-speed multiplier is sim-side
  // (CaptureSystem checks isSignalSurgeActive each tick); this event is the
  // presentation cue.
  private maybeEmitSignalSurgeStarted(): void {
    if (this.sim === null) return;
    if (this.signalSurgeStartedEmitted) return;
    if (!isSignalSurgeActive(this.sim.state.matchSeconds)) return;
    this.signalSurgeStartedEmitted = true;
    this.emitLifecycle({ kind: 'signal_surge_started', tick: this.sim.state.tick });
  }

  // V1.5 doctrine pick window lifecycle. Fires at most once per host-run on each
  // edge transition. Open fires the tick the simulation flips
  // doctrinePickWindowOpen → true; close fires the tick it flips back to false.
  // Subscribers (HUD, bots) react via these events — but state.doctrinePickWindowOpen
  // is the source of truth, so a subscriber that joins late can still read it.
  private maybeEmitDoctrineWindow(): void {
    if (this.sim === null) return;
    const state = this.sim.state;
    if (
      !this.doctrineOpenEmitted &&
      state.doctrinePickWindowOpen &&
      state.doctrinePickWindowClosesAtTick !== null
    ) {
      this.doctrineOpenEmitted = true;
      this.emitLifecycle({
        kind: 'doctrine_window_open',
        tick: state.tick,
        closesAtTick: state.doctrinePickWindowClosesAtTick,
      });
    }
    if (
      !this.doctrineClosedEmitted &&
      this.doctrineOpenEmitted &&
      !state.doctrinePickWindowOpen
    ) {
      this.doctrineClosedEmitted = true;
      this.emitLifecycle({ kind: 'doctrine_window_closed', tick: state.tick });
    }
  }

  private maybeEmitMatchEnded(): void {
    if (this.sim === null) return;
    if (this.endedEmitted) return;
    const outcome = this.sim.state.outcome;
    if (outcome === null) return;
    this.endedEmitted = true;
    this.emitLifecycle({ kind: 'match_ended', tick: this.sim.state.tick, outcome });
  }

  private broadcastSnapshot(): void {
    if (this.sim === null) return;
    const snap = snapshot(this.sim.state);
    if (this.observers.length === 0) {
      // No observers yet — nothing to do until someone registers.
      return;
    }
    for (const playerId of this.observers) {
      for (const fn of this.snapshotListeners) fn(snap, playerId);
    }
  }

  private emitLifecycle(event: MatchLifecycleEvent): void {
    for (const fn of this.lifecycleListeners) fn(event);
  }
}
